import { type SupportMessage } from "../../support/api/support";

export type SupportMessageUpdate = Partial<SupportMessage> & { id: string };

function isCompleteMessage(update: SupportMessageUpdate): update is SupportMessage {
  return Boolean(
    update.conversation_id &&
      update.sender_id &&
      update.sender_role &&
      update.content &&
      update.created_at &&
      update.delivery_state
  );
}

export function upsertMessages(
  existing: SupportMessage[],
  updates: SupportMessageUpdate[]
): SupportMessage[] {
  let next = [...existing];

  for (const update of updates) {
    const index = next.findIndex((message) => message.id === update.id);
    if (index >= 0) {
      next[index] = { ...next[index], ...update };
      continue;
    }

    if (isCompleteMessage(update)) {
      next.push(update);
    }
  }

  return next.sort(
    (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
  );
}

export function getLatestMessage(messages: SupportMessage[]): SupportMessage | null {
  if (messages.length === 0) {
    return null;
  }

  return messages.reduce((latest, current) => {
    const latestTime = new Date(latest.created_at).getTime();
    const currentTime = new Date(current.created_at).getTime();
    if (currentTime > latestTime) {
      return current;
    }
    if (currentTime === latestTime) {
      return current.id > latest.id ? current : latest;
    }
    return latest;
  }, messages[0]);
}

export interface ReadReceiptUpdate {
  conversation_id: string;
  user_id: string;
  last_read_message_id?: string | null;
}

export function applyReadReceipt(
  messages: SupportMessage[],
  receipt: ReadReceiptUpdate,
  viewerId: string,
): SupportMessage[] {
  if (!receipt.last_read_message_id || receipt.user_id === viewerId) {
    return messages;
  }

  const anchor = messages.find((m) => m.id === receipt.last_read_message_id);
  if (!anchor) {
    return messages;
  }

  let changed = false;
  const anchorTime = new Date(anchor.created_at).getTime();

  const next = messages.map((msg) => {
    if (msg.sender_id !== viewerId) {
      return msg;
    }
    const msgTime = new Date(msg.created_at).getTime();
    if (msgTime > anchorTime) {
      return msg;
    }
    if (msgTime === anchorTime && msg.id > anchor.id) {
      return msg;
    }
    if (msg.delivery_state === "read") {
      return msg;
    }
    changed = true;
    return { ...msg, delivery_state: "read" as const };
  });

  return changed ? next : messages;
}
